import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const personalities = {
  guide: { 
    name: "The Guide", 
    color: "#14B8A6", 
    gradient: "from-teal-500 to-teal-600",
    image: "https://static.prod-images.emergentagent.com/jobs/311886e7-2d11-4ea1-a20b-3280d5e92476/images/826c9ffd4257976971a8e3bad649c904f7a03dd263f425f6ab798b5a75141e16.png"
  },
  friend: { 
    name: "The Career Coach", 
    color: "#FACC15", 
    gradient: "from-yellow-400 to-yellow-500",
    image: "https://static.prod-images.emergentagent.com/jobs/311886e7-2d11-4ea1-a20b-3280d5e92476/images/b0a782ead939fb625fd4e33032e723aa8528c7adec3343dbed027220453dad01.png"
  },
  mentor: { 
    name: "The Mentor", 
    color: "#6366F1", 
    gradient: "from-indigo-500 to-indigo-600",
    image: "https://static.prod-images.emergentagent.com/jobs/311886e7-2d11-4ea1-a20b-3280d5e92476/images/7ae80a0192a48d4fe31b8c625ed78bad7b4290254e83888ec3eb5e618261208a.png"
  },
  coach: { 
    name: "The Coach", 
    color: "#EF4444", 
    gradient: "from-red-500 to-red-600",
    image: "https://images.pexels.com/photos/4428040/pexels-photo-4428040.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  }
};

const getStarterPrompts = (personality) => {
  const prompts = {
    guide: [
      "I'm feeling overwhelmed today",
      "Help me practice mindfulness",
      "How can I stay present?",
      "I need to find some calm"
    ],
    friend: [
      "I'm stuck on a career decision",
      "Help me plan my next career move",
      "I'm considering a job change",
      "How do I make better decisions?"
    ],
    mentor: [
      "How can I communicate better?",
      "I want to grow professionally",
      "Help me handle a tough conversation",
      "What skills should I build?"
    ],
    coach: [
      "I want to set goals for this month",
      "Help me build my confidence",
      "I need motivation right now",
      "Let's create an action plan"
    ]
  };
  return prompts[personality] || [];
};

export default function Chat({ user, onLogout }) {
  const { personality, conversationId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(conversationId || null);
  const messagesEndRef = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (conversationId) {
      loadConversationHistory();
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const loadConversationHistory = async () => {
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const historyMessages = response.data.messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      setMessages(historyMessages);
    } catch (error) {
      toast.error("Failed to load conversation history");
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    setInputMessage("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat/message`,
        {
          personality,
          message: userMessage,
          conversation_id: currentConversationId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!currentConversationId) {
        setCurrentConversationId(response.data.conversation_id);
        navigate(`/chat/${personality}/${response.data.conversation_id}`, { replace: true });
      }

      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      toast.error("Failed to send message");
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const currentPersona = personalities[personality];

  if (!currentPersona) {
    return <div className="min-h-screen flex items-center justify-center">Invalid personality</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50 to-pink-50 flex flex-col" data-testid="chat-page">
      <nav className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate("/dashboard")} variant="ghost" size="sm" className="rounded-full" data-testid="back-to-dashboard-btn">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </Button>
              <div className="flex items-center gap-3">
                <img 
                  src={currentPersona.image} 
                  alt={currentPersona.name}
                  className="w-10 h-10 rounded-full object-cover border-2"
                  style={{ borderColor: currentPersona.color }}
                />
                <h1 className="text-xl font-bold" style={{ color: currentPersona.color }}>
                  {currentPersona.name}
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-600">{user.name}</span>
              <Button onClick={onLogout} variant="outline" size="sm" className="rounded-full" data-testid="chat-logout-btn">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="flex-1 max-w-4xl mx-auto w-full px-6 py-8 flex flex-col">
        <div className="flex-1 overflow-y-auto mb-6 space-y-6" data-testid="chat-messages-container">
          {messages.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <div className="w-24 h-24 rounded-full mx-auto mb-4 overflow-hidden border-4" style={{ borderColor: currentPersona.color }}>
                <img src={currentPersona.image} alt={currentPersona.name} className="w-full h-full object-cover" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Hi {user.name}, ready to chat?</h3>
              <p className="text-slate-600 mb-6">I'm {currentPersona.name}. Share what's on your mind, and I'll be here to support you.</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-2xl mx-auto">
                {getStarterPrompts(personality).map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => setInputMessage(prompt)}
                    className="px-4 py-2 rounded-full border-2 text-sm font-medium transition-all hover:scale-105"
                    style={{ 
                      borderColor: currentPersona.color, 
                      color: currentPersona.color,
                      backgroundColor: `${currentPersona.color}10`
                    }}
                    data-testid={`starter-prompt-${idx}`}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
              data-testid={`chat-message-${message.role}-${index}`}
            >
              {message.role === "assistant" && (
                <div className="flex-shrink-0">
                  <img 
                    src={currentPersona.image} 
                    alt={currentPersona.name}
                    className="w-10 h-10 rounded-full object-cover border-2"
                    style={{ borderColor: currentPersona.color }}
                  />
                </div>
              )}
              <div className={message.role === "user" ? "chat-bubble-user max-w-[75%]" : "chat-bubble-ai max-w-[75%]"}>
                {message.role === "assistant" ? (
                  <div className="markdown-content">
                    <ReactMarkdown
                      components={{
                        p: ({node, ...props}) => <p className="mb-3 last:mb-0 leading-relaxed" {...props} />,
                        ul: ({node, ...props}) => <ul className="list-disc list-inside mb-3 space-y-1" {...props} />,
                        ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-3 space-y-1" {...props} />,
                        li: ({node, ...props}) => <li className="ml-2" {...props} />,
                        strong: ({node, ...props}) => <strong className="font-bold text-slate-900" {...props} />,
                        em: ({node, ...props}) => <em className="italic" {...props} />,
                        h1: ({node, ...props}) => <h1 className="text-xl font-bold mb-2" {...props} />,
                        h2: ({node, ...props}) => <h2 className="text-lg font-bold mb-2" {...props} />,
                        h3: ({node, ...props}) => <h3 className="text-base font-bold mb-2" {...props} />,
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </div>
                ) : (
                  message.content
                )}
              </div>
              {message.role === "user" && (
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center text-white font-bold">
                    {user.name.charAt(0).toUpperCase()}
                  </div>
                </div>
              )}
            </motion.div>
          ))}

          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-3 justify-start"
            >
              <div className="flex-shrink-0">
                <img 
                  src={currentPersona.image} 
                  alt={currentPersona.name}
                  className="w-10 h-10 rounded-full object-cover border-2"
                  style={{ borderColor: currentPersona.color }}
                />
              </div>
              <div className="chat-bubble-ai flex items-center gap-2">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></span>
                  <span className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></span>
                </div>
              </div>
            </motion.div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <Card className="p-4 border-2 shadow-lg" data-testid="chat-input-container">
          <form onSubmit={handleSendMessage} className="flex gap-3">
            <Input
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your message..."
              disabled={loading}
              className="flex-1 rounded-full border-slate-200 focus:border-orange-500"
              data-testid="chat-input-field"
            />
            <Button 
              type="submit" 
              disabled={loading || !inputMessage.trim()}
              className="btn-primary rounded-full px-6"
              data-testid="chat-send-btn"
            >
              {loading ? "Sending..." : "Send"}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
}
